import React from 'react';
import { PapiDeviation } from '@wix/da-types-papi';
import { GridElement, StandardGrid } from '@wix/da-shared-react/pkg/Grid';
import { MeasuredCookieType } from '@wix/da-shared-react/pkg/Measured';
import { BiLoggerContextProvider } from '@wix/da-shared-react/pkg/biLogger';
import { ShopItemType } from '../../../../../types/shop';
import ShopThumb, {
  ShopThumbType,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import CommissionThumbWithMenu from '../_partials/CommissionThumbWithMenu';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import LoadableDuperbrowseContext from '../../../../contexts/LoadableDuperbrowseContext';
import EmptyState from '../_partials/EmptyState';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { makeDeviationId } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import {
  CommissionStatusCodeMap,
  CommissionStatusType,
} from '@wix/da-shared-react/pkg/types/commissions';

const CARD_PANEL_HEIGHT = 89;

export const itemTypeToThumbTypeMap = {
  [ShopItemType.PREMIUM_CONTENT]: ShopThumbType.PCP,
  [ShopItemType.COMMISSIONS]: ShopThumbType.COMMISSION,
  [ShopItemType.PRINTS]: ShopThumbType.PRINT,
};

const itemTypeToBiWidgetMap = {
  [ShopItemType.PREMIUM_CONTENT]: 'downloads',
  [ShopItemType.COMMISSIONS]: 'commissions',
  [ShopItemType.PRINTS]: 'prints',
};

export interface Props {
  streamId: string;
  className?: string;
  items: PapiDeviation[];
  itemType: ShopItemType;
  hasMore: boolean;
  isFetching: boolean;
  isOwner: boolean;
  fetchMore: () => void;
  duperbrowseTitle: string;
  emptyState?: React.ReactNode;
}

export class ShopGrid extends React.Component<Props> {
  render() {
    const {
      streamId,
      items,
      itemType,
      hasMore,
      isFetching,
      fetchMore,
      emptyState,
      duperbrowseTitle,
    } = this.props;

    if (!items.length) {
      return emptyState === false
        ? null
        : emptyState || <EmptyState view="large" shopItemType={itemType} />;
    }

    return (
      <ErrorBoundary>
        <LoadableDuperbrowseContext
          streamId={streamId}
          parent={{ title: duperbrowseTitle }}
        >
          <BiLoggerContextProvider
            value={{ widgetname: itemTypeToBiWidgetMap[itemType] }}
          >
            <StandardGrid
              elementCount={items.length}
              cookieType={MeasuredCookieType.USER_PROFILE_SHOP}
              getElementId={this.getItemId}
              getDuperbrowseItemId={this.getDuperbrowseItemId}
              adjustHeight={CARD_PANEL_HEIGHT}
              enableScrollOptim={false}
            >
              {this.renderElement}
            </StandardGrid>
          </BiLoggerContextProvider>
        </LoadableDuperbrowseContext>
        {isFetching && <LoadingIndicator />}
        <OnScrollBottom
          distanceToBottom={2}
          distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
          onScrolledToBottom={hasMore && !isFetching ? fetchMore : undefined}
        />
      </ErrorBoundary>
    );
  }

  renderElement = (element: GridElement) => {
    const { items, itemType, isOwner } = this.props;
    const { index, width, height, style } = element;
    const item = items[index];
    const isCommission = itemType === ShopItemType.COMMISSIONS;
    const commissionStatus = item.commission && item.commission.status;
    const isCommissionDisabled =
      commissionStatus ===
      CommissionStatusCodeMap[CommissionStatusType.DISABLED];

    return (
      <div style={{ ...style }}>
        {isOwner && isCommission ? (
          <CommissionThumbWithMenu
            key={item.deviationId}
            deviation={item}
            width={width}
            height={height}
            disabled={isCommissionDisabled}
            disabledLabel="deviationview.commission.commissionClosed"
          />
        ) : (
          <ShopThumb
            key={index}
            deviation={item}
            width={width}
            height={height}
            type={itemTypeToThumbTypeMap[itemType]}
            showAuthor={false}
            disabled={isCommission && isCommissionDisabled}
            disabledLabel={
              isCommission
                ? 'deviationview.commission.commissionClosed'
                : undefined
            }
          />
        )}
      </div>
    );
  };

  getItemId = (index: number) => {
    const { items = [] } = this.props;
    const item = items[index];
    return `${index}-${item.deviationId}`;
  };

  getDuperbrowseItemId = (index: number) => {
    const { items = [] } = this.props;
    const item = items[index];
    return makeDeviationId(item);
  };
}

export default ShopGrid;
